/**
 * Gateways step 2 — the strategy as a door: the card's one line says the strategy, how order or
 * weights are set, today's rotation (or shares) and the rules; clicking it
 * opens the Flow Optimizer-style window — Formula · Rules · Config (the
 * strategy), the pool ranked live on the right.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { RadioTower } from 'lucide-react';

import { StepCard } from '@/components/ui';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import { OptimizerDialog } from '../optimizer-dialog/OptimizerDialog';
import type { ContainerEditor } from '../state/useContainerEditor';
import { gwLetter } from './cards/strategy-copy';
import { poolShares, rankingNeeded, rotationOrder } from './ranking/formula-pool';
import { GatewayStrategyContent } from './ranking/GatewayStrategyContent';
import type { GatewayRankingQuery } from './ranking/use-gateway-ranking';

interface Props {
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  containerId: string | null;
  countryKey: string;
  /** The strategy in words — "Hold until it fails · drag order · 1 rule". */
  summary: string;
  /** Enabled, unpinned slots — what SPLIT divides. */
  splitOver: number;
  ranking: GatewayRankingQuery;
  routeName: (routeId: number) => string;
}

/** "A → B → C" (rotation) or "A 40% · B 60%" (shares) — what the day uses today. */
function todayLine(p: Props): string | null {
  const gw = p.editor.draft.gateway;
  if (gw.pool.length === 0) return 'no gateways yet';
  const preview = rankingNeeded(gw) ? p.ranking.data : undefined;
  const view = rotationOrder(gw, preview);
  if (gw.strategy !== 'RANDOM' && gw.strategy !== 'SPLIT') return view.order.map(gwLetter).join(' → ');
  const shares = poolShares(gw, preview);
  const total = shares.reduce((a, b) => a + b, 0) || 1;
  return view.order.map((i) => `${gwLetter(i)} ${Math.round((shares[i] / total) * 100)}%`).join(' · ');
}

export const StrategyStep: FC<Props> = (p) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <StepCard index={2} icon={RadioTower} title="Strategy" summary={[p.summary, todayLine(p)].filter(Boolean).join(' · ')} onLaunch={() => setOpen(true)} />
      <OptimizerDialog open={open} onOpenChange={setOpen}>
        <GatewayStrategyContent editor={p.editor} plan={p.plan} containerId={p.containerId} countryKey={p.countryKey} splitOver={p.splitOver} routeName={p.routeName} onClose={() => setOpen(false)} />
      </OptimizerDialog>
    </>
  );
};
